export const MATCH_POINTS = 10;
export const MISMATCH_FLIP_DELAY_MS = 800;

export const GAME_DIFFICULTIES = {
  easy: { id: "easy", label: "Easy", pairs: 3, cards: 6, rows: 3, cols: 2, timeSeconds: 30 },
  medium: { id: "medium", label: "Medium", pairs: 6, cards: 12, rows: 3, cols: 4, timeSeconds: 45 },
};

const IMAGE_FIELDS = [
  "animated_image_url",
  "handdrawn_image_url",
  "hand_drawn_image_url",
  "animated_image",
  "handdrawn_image",
  "image_url",
  "thumbnail_url",
  "avatar_url",
  "cover_url",
  "photo_url",
  "generated_image_url",
  "reference_image_url",
  "display_image_url",
  "preview_image_url",
  "prop_image_url",
  "asset_image_url",
  "character_image",
  "image",
  "imageUrl",
  "imagePath",
  "asset_url",
  "file_url",
  "url",
  "src",
];

// Priority chain used consistently across the app for character portraits
// (see Characters.js, CreateStory.js, etc.)
const CHARACTER_IMAGE_FIELDS = [
  "animated_image_url",
  "handdrawn_image_url",
  "hand_drawn_image_url",
  "image_url",
  "avatar_url",
  "animated_image",
  "handdrawn_image",
];

const pickCharacterImageUrl = (character) => {
  if (!character || typeof character !== "object") return null;
  for (const field of CHARACTER_IMAGE_FIELDS) {
    if (typeof character[field] === "string" && character[field].trim()) {
      return character[field].trim();
    }
  }
  return pickAssetImageUrl(character);
};

const pickAssetImageUrl = (item) => {
  if (!item || typeof item !== "object") return null;
  for (const field of IMAGE_FIELDS) {
    if (typeof item[field] === "string" && item[field].trim()) {
      return item[field].trim();
    }
  }
  return null;
};

const pickAssetLabel = (item, fallback = "Story Asset") =>
  item?.name || item?.title || item?.label || item?.display_name || item?.prop_name || fallback;

const normalizeImageValue = (value) => (typeof value === "string" ? value.trim() : "");

const looksLikeImageUrl = (value) =>
  typeof value === "string" && /(\.png|\.jpe?g|\.webp|\.gif)(\?.*)?$/i.test(value);

const looksLikeRemoteOrBucketImage = (value) =>
  typeof value === "string" &&
  (looksLikeImageUrl(value) || /^https?:\/\//i.test(value) || /^\/(assets|storage|uploads)\//i.test(value));

const pushAsset = (target, imageUrl, label, raw = null, type = "asset") => {
  const normalized = normalizeImageValue(imageUrl);
  if (!normalized || !looksLikeRemoteOrBucketImage(normalized)) return;
  target.push({ imageUrl: normalized, label, raw, type });
};

const collectImageEntriesDeep = (value, fallbackName, entries = []) => {
  if (!value) return entries;

  if (Array.isArray(value)) {
    value.forEach((entry, index) =>
      collectImageEntriesDeep(entry, `${fallbackName} ${index + 1}`, entries)
    );
    return entries;
  }

  if (typeof value === "object") {
    const fallback = pickAssetLabel(value, fallbackName);
    const direct = pickAssetImageUrl(value);
    if (direct) {
      entries.push({ raw: value, imageUrl: direct, label: fallback });
    }

    Object.entries(value).forEach(([key, child]) => {
      if (typeof child === "string" && IMAGE_FIELDS.includes(key)) {
        entries.push({
          raw: value,
          imageUrl: child,
          label: `${fallback} (${key.replace(/_/g, " ")})`,
        });
        return;
      }
      collectImageEntriesDeep(child, `${fallback} ${key}`, entries);
    });
    return entries;
  }

  if (looksLikeRemoteOrBucketImage(value)) {
    entries.push({ raw: value, imageUrl: value, label: fallbackName });
  }
  return entries;
};

const dedupeAssetsByImage = (assets) => {
  const seen = new Set();
  const deduped = [];
  assets.forEach((asset) => {
    const imageKey = normalizeImageValue(asset?.imageUrl);
    if (!imageKey || seen.has(imageKey)) return;
    seen.add(imageKey);
    deduped.push(asset);
  });
  return deduped;
};

const extractCharacterAssets = (characters) => {
  const assets = [];
  characters.forEach((character, index) => {
    const baseName = pickAssetLabel(character, `Character ${index + 1}`);

    // Every distinct image field on the character (animated, handdrawn, thumbnail, etc.)
    // becomes its own memory asset so we have as many unique images as possible per
    // character, instead of only ever picking one and starving the pool.
    CHARACTER_IMAGE_FIELDS.forEach((field) => {
      if (typeof character?.[field] === "string" && character[field].trim()) {
        pushAsset(
          assets,
          character[field],
          field === "animated_image_url" || field === "animated_image"
            ? baseName
            : `${baseName} (${field.replace(/_?image_?url$|_?image$/i, "").replace(/_/g, " ") || field})`,
          character,
          "character"
        );
      }
    });

    if (!assets.some((asset) => asset.raw === character)) {
      pushAsset(assets, pickCharacterImageUrl(character), baseName, character, "character");
    }

    const variants = character?.variants;
    if (variants && typeof variants === "object") {
      Object.entries(variants).forEach(([variantName, variantValue]) => {
        if (typeof variantValue === "string") {
          pushAsset(assets, variantValue, `${baseName} (${variantName})`, character, "character");
          return;
        }
        if (variantValue && typeof variantValue === "object") {
          const variantImage = pickAssetImageUrl(variantValue);
          pushAsset(assets, variantImage, `${baseName} (${variantName})`, variantValue, "character");
        }
      });
    }
  });
  return assets;
};

const extractPropAssets = (props) =>
  collectImageEntriesDeep(props, "Prop")
    .map((entry, index) => ({
      id: `prop-${index + 1}`,
      type: "prop",
      raw: entry.raw,
      imageUrl: entry.imageUrl,
      label: entry.label || `Prop ${index + 1}`,
    }))
    .filter((entry) => Boolean(entry.imageUrl));

const extractBackgroundAssets = (backgroundRefs) =>
  collectImageEntriesDeep(backgroundRefs, "Background Character")
    .map((entry, index) => ({
      id: `background-${index + 1}`,
      type: "background",
      raw: entry.raw,
      imageUrl: entry.imageUrl,
      label: entry.label || `Background Character ${index + 1}`,
    }))
    .filter((entry) => Boolean(entry.imageUrl));

export const formatTime = (seconds) => {
  const safe = Math.max(0, Number(seconds) || 0);
  const mins = Math.floor(safe / 60);
  const secs = safe % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

export const shuffle = (list) => {
  const arr = [...list];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

export const extractMemoryAssetsFromGamesPayload = (payload) => {
  const characters = Array.isArray(payload?.characters) ? payload.characters : [];
  const characterAssets = extractCharacterAssets(characters);
  const propAssets = extractPropAssets(payload?.props);
  const backgroundAssets = extractBackgroundAssets(payload?.background_character_refs);

  // Priority:
  // 1) character assets + variants
  // 2) props
  // 3) background character refs
  return dedupeAssetsByImage([...characterAssets, ...propAssets, ...backgroundAssets]).map(
    (asset, index) => ({
      id: asset.id || `${asset.type || "asset"}-${index + 1}`,
      type: asset.type || "asset",
      ...asset,
    })
  );
};

export const buildMemoryDeckFromAssets = (sourceAssets, pairsCount) => {
  if (!Array.isArray(sourceAssets) || !sourceAssets.length || pairsCount <= 0) {
    return { deck: [], hasAssets: false };
  }

  const randomizedPool = shuffle(sourceAssets);
  const selectedAssets = [];

  if (randomizedPool.length >= pairsCount) {
    selectedAssets.push(...randomizedPool.slice(0, pairsCount));
  } else {
    // Not enough unique assets to avoid duplication entirely. Use every unique
    // asset once, then cycle through a freshly shuffled copy of the pool so
    // duplicates are spread evenly instead of the same asset repeating over
    // and over (e.g. always "Tigey").
    selectedAssets.push(...randomizedPool);
    let cycleIndex = 0;
    let dupPool = shuffle(randomizedPool);
    while (selectedAssets.length < pairsCount) {
      if (cycleIndex >= dupPool.length) {
        dupPool = shuffle(randomizedPool);
        cycleIndex = 0;
      }
      selectedAssets.push(dupPool[cycleIndex]);
      cycleIndex += 1;
    }
  }

  const pairs = selectedAssets.map((asset, index) => ({
    ...asset,
    pairKey: `pair-${index + 1}`,
  }));

  const cards = pairs.flatMap((asset, index) => [
    {
      id: `${asset.pairKey}-a-${index}`,
      pairKey: asset.pairKey,
      imageUrl: asset.imageUrl,
      label: asset.label,
      type: asset.type,
    },
    {
      id: `${asset.pairKey}-b-${index}`,
      pairKey: asset.pairKey,
      imageUrl: asset.imageUrl,
      label: asset.label,
      type: asset.type,
    },
  ]);

  return {
    deck: shuffle(cards),
    hasAssets: true,
  };
};

export const buildMemoryDeckFromGamesPayload = (payload, difficultyConfig) => {
  const sourceAssets = extractMemoryAssetsFromGamesPayload(payload);
  const deckResult = buildMemoryDeckFromAssets(
    sourceAssets,
    difficultyConfig?.pairs || GAME_DIFFICULTIES.easy.pairs
  );
  return {
    ...deckResult,
    storyId: payload?.story?.id || null,
    sourceAssets,
  };
};
